/**
 * objectives.ts — The level's objective state machine, as a pure reducer.
 *
 * Extracted from Game.ts, which can only be constructed with a live WebGL
 * context — so none of this was reachable from a unit test, despite being the
 * spine of the whole run:
 *
 *   WALKING → (dog goes, walker bags it) → CARRYING_BAG
 *           → (reach the bin) → GO_HOME → (reach home) → WON
 *
 * …plus the terminal loss, when a car touches the walker or the dog, or the
 * dog works itself up into a full frenzy and gets away.
 *
 * ## Going and bagging it are one beat
 *
 * There was a `CLEANUP_PENDING` phase between the first two, in which the
 * player held a control near the mess for three seconds to pick it up. It is
 * gone, and `types.ts` → {@link Objective} says why in full: the walker is
 * clamped within a lead's length of a dog that is going, so the range test was
 * always satisfied and the hold was three seconds of standing still on top of
 * the three the squat had just cost. What is left is a countdown — the walker
 * bending down — which is why the only thing this reducer needs from the world
 * during that phase is the frame delta.
 *
 * The reducer owns *what happens next* and nothing else. It never touches
 * entities, the scene, or the emitter: each step returns the next state plus a
 * list of {@link ObjectiveEffect}s, and Game.ts turns those into particle
 * bursts, marker visibility, and HUD events. That split is what makes the rules
 * testable without a renderer — and it's the same "extract the pure part" rule
 * the rest of the codebase already follows (see AGENTS.md → Testing).
 *
 * Tuning lives in config/constants.ts → OBJECTIVE.
 */
import { OBJECTIVE } from '../config/constants';
import { Objective, type LossReason } from '../types';

/** Everything the machine needs to remember between frames. */
export interface ObjectiveState {
  readonly objective: Objective;
  /**
   * Milliseconds left of the scoop, counting down. Zero at every other point in
   * the run — including before the dog has been, which is what makes it safe to
   * read as "is the walker bagging it up right now".
   */
  readonly scoopMs: number;
  /** Terminal: the run is over (won or hit by a car) and the sim is frozen. */
  readonly ended: boolean;
}

/** What the world looks like this frame, as far as the objectives care. */
export interface ObjectiveInput {
  /** Frame delta in milliseconds (already clamped by the caller). */
  readonly deltaMs: number;
  /** Is the walker standing at the bin? */
  readonly atBin: boolean;
  /** Is the walker inside the home zone? */
  readonly atHome: boolean;
}

/**
 * Something the caller must make happen. Deliberately declarative: the reducer
 * says "the mess is gone and the bag is in hand", not "sparkle at (x, y)".
 */
export type ObjectiveEffect =
  /** The objective changed — update markers and tell the HUD. */
  | { readonly type: 'objective'; readonly objective: Objective }
  /** The dog has been: put the mess on the pavement, the walker is on it. */
  | { readonly type: 'poop-landed' }
  /** Bagged: clear the mess, hand over the bag, let the dog walk on. */
  | { readonly type: 'scooped' }
  /** The bag went in the bin — play the pop at the bin. */
  | { readonly type: 'binned' }
  /** The run was won. */
  | { readonly type: 'won' }
  /** The run was lost — the reason decides what the overlay says. */
  | { readonly type: 'lost'; readonly reason: LossReason };

/** State plus the effects produced getting there. */
export type ObjectiveStep = readonly [ObjectiveState, readonly ObjectiveEffect[]];

/** The state a fresh run starts in. */
export function initialObjectiveState(): ObjectiveState {
  return { objective: Objective.WALKING, scoopMs: 0, ended: false };
}

/**
 * The dog pooped — start the scoop. Only meaningful during the free-roam phase
 * and only once: a poop announced at any other point (a re-entered state, a dog
 * that has already been) changes nothing and produces no effects, which is what
 * `Game` leans on to avoid dropping a second mess in the world.
 */
export function notePoop(state: ObjectiveState): ObjectiveStep {
  if (state.ended || state.objective !== Objective.WALKING || state.scoopMs > 0) {
    return [state, []];
  }
  return [{ ...state, scoopMs: OBJECTIVE.SCOOP_MS }, [{ type: 'poop-landed' }]];
}

/**
 * Advance one frame.
 *
 * Only the phase-specific rule for the current objective runs; everything else
 * is inert, which is what stops (say) walking past the bin before the bag exists
 * from skipping the errand entirely.
 */
export function stepObjective(state: ObjectiveState, input: ObjectiveInput): ObjectiveStep {
  if (state.ended) return [state, []];

  switch (state.objective) {
    case Objective.WALKING:
      // Nothing to do during free roam itself — the objective only moves once
      // the dog has been, and `scoopMs` is what says it has.
      return state.scoopMs > 0 ? stepScoop(state, input) : [state, []];
    case Objective.CARRYING_BAG:
      return input.atBin
        ? prepend({ type: 'binned' }, transitionTo(state, Objective.GO_HOME))
        : [state, []];
    case Objective.GO_HOME:
      return input.atHome ? transitionTo(state, Objective.WON) : [state, []];
    default:
      // `WON` only, and only in theory: winning sets `ended`, which the guard
      // above already answers. Here so the switch stays total.
      return [state, []];
  }
}

/**
 * Count the scoop down, and hand over the bag when it lands.
 *
 * Frame-driven rather than a wall-clock timer, deliberately: this runs from
 * `simulate()`, which is skipped once the run has ended, so a car that hits the
 * walker mid-scoop stops it dead instead of handing a bag to a finished run.
 */
function stepScoop(state: ObjectiveState, input: ObjectiveInput): ObjectiveStep {
  const scoopMs = state.scoopMs - input.deltaMs;
  if (scoopMs > 0) return [{ ...state, scoopMs }, []];
  return prepend(
    { type: 'scooped' },
    transitionTo({ ...state, scoopMs: 0 }, Objective.CARRYING_BAG),
  );
}

/**
 * The run ended badly — a car hit the walker or the dog, or the frenzy meter
 * filled and the dog is gone. Idempotent: a second caller on the same frame
 * must not fire a second game over, which is why both losses come through here
 * rather than each one setting the flag itself.
 */
export function endRun(state: ObjectiveState, reason: LossReason = 'car'): ObjectiveStep {
  if (state.ended) return [state, []];
  return [{ ...state, ended: true }, [{ type: 'lost', reason }]];
}

/** Move to `next`, emitting the objective change and any terminal effect. */
function transitionTo(state: ObjectiveState, next: Objective): ObjectiveStep {
  const effects: ObjectiveEffect[] = [{ type: 'objective', objective: next }];
  const won = next === Objective.WON;
  if (won) effects.push({ type: 'won' });
  return [{ ...state, objective: next, ended: state.ended || won }, effects];
}

function prepend(effect: ObjectiveEffect, [state, effects]: ObjectiveStep): ObjectiveStep {
  return [state, [effect, ...effects]];
}
